// Pure helpers that derive per-timestamp and day-level statistics from the
// MarketForecasterSlotMatrix `matrix_json` payload returned by
// `GET /market/forecaster-slot-matrix/list`.
//
// The pipeline keeps the matrix close to the raw evidence (residuals, lead
// hours, fill source) and lets the read side derive the scoring numbers, so
// the same math can be reproduced in tests and in the UI without a backend
// round-trip.
//
// Aggregation rule (matches `forecast/src/assessment/per_hour_scoring.py` and
// `documentation/docs/evaluation*.md`):
//   - Per timestamp: a plain ARITHMETIC MEAN of the squared residuals over the
//     filled slots — every slot counts the same, regardless of lead time. For
//     Q50/RMSE this per-timestamp value is the MSE (mean squared residual); the
//     square root is applied once, at the day layer.
//   - Per day: sqrt( mean over timestamps of the per-timestamp MSE ).
//
// Lead time is retained for display only (it labels each slot) and never enters
// the score. Legacy payloads carrying `weighting:"linear"` predate the move to
// the arithmetic mean and are scored the same way — the field is ignored.

export const SOURCES = Object.freeze({
  LIVE: 'live',
  FWD_FILL: 'fwd_fill',
  CROSS_HORIZON: 'cross_horizon',
  MISSING: 'missing',
  // No intraday session ever closed at this slot's gate hour (e.g. the
  // cold-start boundary on the first operational day). The slot is not a
  // real submission opportunity: it is exempt from coverage and excluded
  // from the per-timestamp mean — distinct from "missing" (a session-backed
  // slot the forecaster left uncovered, which DOES fail the day).
  NOT_APPLICABLE: 'not_applicable',
});

const isFilled = (source) =>
  !!source && source !== SOURCES.MISSING && source !== SOURCES.NOT_APPLICABLE;

const isApplicable = (source) => source !== SOURCES.NOT_APPLICABLE;

const safeNumber = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * Validate the matrix_json structure and return a normalized view. Returns
 * null if the payload is missing or malformed (caller should render an
 * empty/error state).
 */
export function normalizeMatrix(matrixJson) {
  if (!matrixJson || typeof matrixJson !== 'object') return null;
  const { timestamps, slots_per_timestamp, residuals, sources, lead_hours } = matrixJson;
  if (!Array.isArray(timestamps) || !Array.isArray(residuals) || !Array.isArray(sources) || !Array.isArray(lead_hours)) {
    return null;
  }
  if (timestamps.length !== residuals.length || timestamps.length !== sources.length || timestamps.length !== lead_hours.length) {
    return null;
  }
  const slotCount = typeof slots_per_timestamp === 'number' ? slots_per_timestamp : 24;
  return {
    timestamps,
    slotCount,
    residuals,
    sources,
    leadHours: lead_hours,
  };
}

/**
 * Mean of the squared residuals over the *filled* slots of one timestamp row.
 * Returns null when no slot is filled (matches the pipeline's "skip the
 * timestamp" convention).
 */
function meanSquaredResidual(residualRow, sourceRow, slotCount) {
  let sumSquares = 0;
  let filled = 0;
  for (let s = 0; s < slotCount; s += 1) {
    if (!isFilled(sourceRow[s])) continue;
    const r = safeNumber(residualRow[s]);
    if (r == null) continue;
    sumSquares += r * r;
    filled += 1;
  }
  return { mse: filled > 0 ? sumSquares / filled : null, filled };
}

/**
 * Per-timestamp scores. For each row returns:
 *   - `mse`:  the arithmetic mean of squared residuals over filled slots
 *             (the Layer-2 value; null when no slot is filled),
 *   - `rmse`: sqrt(mse), provided for callers that want a same-unit number,
 *   - `filledSlots` / `missingSlots`: coverage counts for the row.
 */
export function perTimestampScores(matrix) {
  const m = normalizeMatrix(matrix);
  if (!m) return [];
  const out = [];
  for (let t = 0; t < m.timestamps.length; t += 1) {
    const { mse, filled } = meanSquaredResidual(
      m.residuals[t] || [],
      m.sources[t] || [],
      m.slotCount
    );
    out.push({
      timestamp: m.timestamps[t],
      mse,
      rmse: mse == null ? null : Math.sqrt(mse),
      filledSlots: filled,
      missingSlots: m.slotCount - filled,
    });
  }
  return out;
}

/**
 * Day-level RMSE: sqrt( mean over timestamps of the per-timestamp MSE ).
 * Timestamps with no filled slots are excluded from the day average. Returns
 * null when no timestamp has a score.
 */
export function dayScore(matrix) {
  const m = normalizeMatrix(matrix);
  if (!m) return null;
  let mseSum = 0;
  let count = 0;
  for (let t = 0; t < m.timestamps.length; t += 1) {
    const { mse } = meanSquaredResidual(
      m.residuals[t] || [],
      m.sources[t] || [],
      m.slotCount
    );
    if (mse != null) {
      mseSum += mse;
      count += 1;
    }
  }
  return count > 0 ? Math.sqrt(mseSum / count) : null;
}

/**
 * Counts of slots by fill source plus a coverage flag. `fullyCovered` is
 * the Rule 5 qualification check at the matrix level.
 */
export function slotCounts(matrix) {
  const m = normalizeMatrix(matrix);
  const empty = {
    live: 0,
    fwd_fill: 0,
    cross_horizon: 0,
    missing: 0,
    not_applicable: 0,
    total: 0,
    applicable: 0,
    filled: 0,
    fullyCovered: false,
  };
  if (!m) return empty;
  let live = 0;
  let fwdFill = 0;
  let cross = 0;
  let missing = 0;
  let notApplicable = 0;
  for (let t = 0; t < m.timestamps.length; t += 1) {
    const sourceRow = m.sources[t] || [];
    for (let s = 0; s < m.slotCount; s += 1) {
      switch (sourceRow[s]) {
        case SOURCES.LIVE: live += 1; break;
        case SOURCES.FWD_FILL: fwdFill += 1; break;
        case SOURCES.CROSS_HORIZON: cross += 1; break;
        case SOURCES.NOT_APPLICABLE: notApplicable += 1; break;
        default: missing += 1; break;
      }
    }
  }
  const total = m.timestamps.length * m.slotCount;
  const filled = live + fwdFill + cross;
  const applicable = total - notApplicable;
  return {
    live,
    fwd_fill: fwdFill,
    cross_horizon: cross,
    missing,
    not_applicable: notApplicable,
    total,
    applicable,
    filled,
    // A day qualifies when every *applicable* (session-backed) slot is
    // filled. not_applicable slots — no intraday session at that gate — are
    // exempt, so a cold-start day with no real misses is still fully covered.
    fullyCovered: missing === 0 && applicable > 0,
  };
}

export const __testables = { isFilled, isApplicable, safeNumber, meanSquaredResidual };
